import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  FormControl,
  Autocomplete,
  Paper,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
  InputAdornment,
  useMediaQuery,
  styled,
  debounce,
  Button,
} from '@mui/material';
import { PatternFormat } from 'react-number-format';
import { ArrowBack, CancelOutlined, HelpOutline, EditOutlined } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { RichTextEditor, type RichTextEditorRef } from 'mui-tiptap';
import StarterKit from '@tiptap/starter-kit';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ButtonAtom from '../../components/atoms/Button';
import Theme from '../../theme/Theme';
import { CityService } from '../../services/city/CityService';
import { EventService } from '../../services/event/EventService';
import { RegionService } from '../../services/region/RegionService';
import { DistrictService } from '../../services/district/DistrictService';
import { PlaceService } from '../../services/place/PlaceService';
import httpClient from '../../apis/base/httpClient';
import { MediaService } from '../../services/media/MediaService';
import { MediaTypeEnum } from '../../enums/MediaTypeEnum';
import { LoginService } from '../../services/LoginService';
import { PlaceTypeEnum } from '../../enums/PlaceTypeEnum';
import { EstabilishmentService } from '../../services/estabilishment/EstabilishmentService';
import PaymentFullScreenModal from '../template/PaymentFullScreenModalProps';
import { Plan, plans } from '../../interfaces/Plan';

// ==================== TYPES ====================
type PlaceFormProps = {
  mode: 'create' | 'edit';
  placeId?: number;
  selectedPlan?: { placeType: PlaceTypeEnum } | null;
  promotionalCode?: string;
  bypassPaymentFlow?: boolean;
};

type ImageItem = {
  id?: number | string;
  file?: File | null;
  url: string;
};

type Folder = {
  id?: number;
  name: string;
  images: ImageItem[];
  canExclude: boolean;
};

type FormDataType = {
  id?: number;
  description: string;
  address: string;
  addressNumber: string;
  postalCode: string;
  complement: string;
  minCapacity: number;
  maxCapacity: number;
  hasAirConditioner: boolean;
  hasBuffet: boolean;
  hasParking: boolean;
  hasOutdoor: boolean;
  hasSound: boolean;
  hasRoom: boolean;
  area: number;
  socialReason: string;
  fantasyName: string;
  enterpriseNumber: string;
  responsibleName: string;
  responsibleEmail: string;
  responsiblePhone: string;
  cityId: number;
  regionId: number;
  districtId: number;
  events: any[];
  userAccountId: number;
  youtube: string;
  estabilishmentType: number;
  status: string;
  type: PlaceTypeEnum;
};

// ==================== MAIN COMPONENT ====================
export default function PlaceForm({
  mode,
  placeId,
  selectedPlan,
  promotionalCode,
  bypassPaymentFlow = false,
}: PlaceFormProps) {
  // ==================== NAVIGATION & AUTH ====================
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlPromotionalCode = searchParams.get('promocao')?.trim() || '';
  const effectivePromotionalCode = promotionalCode || urlPromotionalCode;
  const shouldRedirectWithBackpack = urlPromotionalCode === 'backpack-pocket';

  if (shouldRedirectWithBackpack) {
    selectedPlan = { placeType: PlaceTypeEnum.FREE };
  }

  if (!LoginService.isAuthenticated()) {
    LoginService.logout();
    if (shouldRedirectWithBackpack) {
      navigate('/login/?redirect=/cadastrar/espaco?promocao=backpack-pocket');
    } else {
      navigate('/login');
    }
  }

  // ==================== THEME & RESPONSIVE ====================
  const token: string | null = localStorage.getItem('token');
  const theme = Theme;
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ==================== STEP MANAGEMENT ====================
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Dados Básicos', 'Endereço', 'Informações Adicionais', 'Mídias'];
  const [canJumpToNext, setCanJumpToNext] = useState(false);
  const [expandValidationReasons, setExpandValidationReasons] = useState(false);

  // ==================== EDITOR REF ====================
  const editorDomRef = useRef<RichTextEditorRef | null>(null);
  const MAX_LENGTH = 1000;
  const [value, setValue] = useState('');

  // ==================== LOADING STATES ====================
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(mode === 'edit' && !!placeId);
  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState('');
  const [cnpjError, setCnpjError] = useState(false);
  const [cnpjHelperText, setCnpjHelperText] = useState('');
  const [cnpjVerifying, setCnpjVerifying] = useState(false);
  const [cnpjAvailable, setCnpjAvailable] = useState<boolean | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [bairroViaCep, setBairroViaCep] = useState<string>();
  const [cidadeViaCep, setCidadeViaCep] = useState<string>();

  // ==================== PAYMENT MODAL STATES ====================
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [paymentPlaceId, setPaymentPlaceId] = useState<number | null>(null);
  const [paymentPlan, setPaymentPlan] = useState<Plan | null>(null);
  const [originalPlaceType, setOriginalPlaceType] = useState<string | null>(null);
  const [originalEnterpriseNumber, setOriginalEnterpriseNumber] = useState<string>('');

  // ==================== DROPDOWN STATES ====================
  const [cities, setCities] = useState<any[]>([]);
  const [regions, setRegions] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [estabilishments, setEstablishments] = useState<any[]>([]);

  // ==================== FORM DATA ====================
  const [formData, setFormData] = useState<FormDataType>({
    description: '',
    address: '',
    addressNumber: '',
    postalCode: '',
    complement: '',
    minCapacity: 10,
    maxCapacity: 200,
    hasAirConditioner: false,
    hasBuffet: false,
    hasParking: false,
    hasOutdoor: false,
    hasSound: false,
    hasRoom: false,
    area: 0,
    socialReason: '',
    fantasyName: '',
    enterpriseNumber: '',
    responsibleName: '',
    responsibleEmail: '',
    responsiblePhone: '',
    cityId: 0,
    regionId: 0,
    districtId: 0,
    events: [],
    userAccountId: 1,
    youtube: '',
    estabilishmentType: 0,
    status: 'INACTIVE',
    type: selectedPlan ? selectedPlan.placeType : PlaceTypeEnum.FREE,
  });

  // ==================== MEDIA STATES ====================
  const [blueprint, setBlueprint] = useState<ImageItem | null>(null);
  const [banner, setBanner] = useState<ImageItem | null>(null);
  const [brand, setBrand] = useState<ImageItem | null>(null);
  const [folders, setFolders] = useState<Folder[]>([
    // { name: 'Nosso Espaço', images: [], canExclude: true }
  ]);

  // ==================== TRACKING CHANGES ====================
  const [imagesToRemove, setImagesToRemove] = useState<{ imageId: number; folderId?: number }[]>(
    [],
  );
  const [foldersToRemove, setFoldersToRemove] = useState<number[]>([]);
  const [foldersToRename, setFoldersToRename] = useState<{ id: number; newName: string }[]>([]);

  // ==================== CONSTANTS ====================
  const IMAGE_MAX_LIMIT = 10;
  const IMAGE_MIN_LIMIT = 6;
  const IMAGE_SIZE_LIMIT_MB = 60000000;

  // ==================== STYLED COMPONENTS ====================
  const HeaderImage = styled('img')(() => ({
    width: '100%',
    height: 300,
    objectFit: 'cover',
    backgroundPosition: 'center',
    boxShadow: '2px 2px 6px -2px',
    display: 'block',
  }));

  const BrandImage = styled('img')(({ theme }) => ({
    width: '100%',
    height: 'auto',
    objectFit: 'cover',
    backgroundPosition: 'center',
    boxShadow: '2px 2px 6px -2px',
    display: 'block',
  }));

  const SquareImage = styled('img')(({ theme }) => ({
    width: '100%',
    height: 'auto',
    objectFit: 'cover',
    backgroundPosition: 'center',
    boxShadow: '2px 2px 6px -2px',
    display: 'block',
  }));

  // ==================== PLAN RULES ====================
  const PLAN_RULES = () => {
    const placeType = selectedPlan?.placeType || formData.type;
    switch (placeType) {
      case PlaceTypeEnum.FREE:
        return { maxFolders: 0, banner: false, blueprint: false, video: false };
      case PlaceTypeEnum.PARTNER:
        return { maxFolders: 1, banner: true, blueprint: false, video: false };
      case PlaceTypeEnum.BASIC:
        return { maxFolders: 1, banner: true, blueprint: true, video: false };
      case PlaceTypeEnum.PREMIUM:
        return { maxFolders: 1, banner: true, blueprint: true, video: true };
      default:
        return { maxFolders: 0, banner: false, blueprint: false, video: false };
    }
  };

  // ==================== UTILITY FUNCTIONS ====================
  const normalize = (s?: string) => {
    if (!s) return '';
    return s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  };

  const fetchAddressByCep = async (cep: string) => {
    if (cep.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();
        if (!data.erro) {
          console.log('📍 Dados do ViaCEP:', data);
          const responsiblePhoneDigits = (data.responsiblePhone ?? '').replace(/\D/g, '');
          const normalizedResponsiblePhone = responsiblePhoneDigits.startsWith('55')
            ? responsiblePhoneDigits.slice(2)
            : responsiblePhoneDigits;

          setFormData((prev) => ({
            ...prev,
            address: data.logradouro,
            complement: data.complemento,
          }));
          if (data.bairro) {
            setBairroViaCep(normalize(data.bairro));
          }
          if (data.localidade) {
            const cidadeNormalizada = normalize(data.localidade);
            console.log('🏙️ Cidade ViaCEP original:', data.localidade);
            console.log('🏙️ Cidade ViaCEP normalizada:', cidadeNormalizada);
            setCidadeViaCep(cidadeNormalizada);
          }
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };

  // ==================== FORM VALIDATION ====================
  const handleBlur = (field: string) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const validateCNPJ = (cnpj: string): boolean => {
    // Remove caracteres não numéricos
    const cleanCNPJ = cnpj.replace(/\D/g, '');

    // Valida comprimento
    if (cleanCNPJ.length !== 14) return false;

    // Valida se todos os dígitos são iguais
    if (/^(\d)\1{13}$/.test(cleanCNPJ)) return false;

    // Calcula primeiro dígito verificador
    let size = cleanCNPJ.length - 2;
    let numbers = cleanCNPJ.substring(0, size);
    const digits = cleanCNPJ.substring(size);
    let sum = 0;
    let pos = size - 7;

    for (let i = size; i >= 1; i--) {
      sum += parseInt(numbers.charAt(size - i)) * pos--;
      if (pos < 2) pos = 9;
    }

    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(digits.charAt(0))) return false;

    // Calcula segundo dígito verificador
    size = size + 1;
    numbers = cleanCNPJ.substring(0, size);
    sum = 0;
    pos = size - 7;

    for (let i = size; i >= 1; i--) {
      sum += parseInt(numbers.charAt(size - i)) * pos--;
      if (pos < 2) pos = 9;
    }

    result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(digits.charAt(1))) return false;

    return true;
  };

  const getError = (field: string) => {
    if (!touched[field]) return '';
    if (
      !formData[field as keyof FormDataType] ||
      !formData[field as keyof FormDataType]?.toString()
    ) {
      return 'Campo obrigatório';
    }
    return '';
  };

  const verifyCNPJAvailability = async (cnpj: string) => {
    const cleanCNPJ = cnpj.replace(/\D/g, '');
    if (cleanCNPJ.length !== 14) return;

    const cleanOriginalCNPJ = originalEnterpriseNumber.replace(/\D/g, '');
    if (mode === 'edit' && cleanOriginalCNPJ && cleanCNPJ === cleanOriginalCNPJ) {
      setCnpjAvailable(true);
      setCnpjHelperText('');
      setCnpjError(false);
      setCnpjVerifying(false);
      return;
    }

    try {
      setCnpjVerifying(true);
      setCnpjAvailable(null);
      const response = await httpClient.get(`/place/verify/${cleanCNPJ}`);
      const result = response.data;

      if (result?.available === false) {
        setCnpjAvailable(false);
        setCnpjHelperText('Este CNPJ já está cadastrado');
        setCnpjError(true);
      } else {
        setCnpjAvailable(true);
        setCnpjHelperText('CNPJ disponível');
        setCnpjError(false);
      }
    } catch (error) {
      console.error('Erro ao verificar CNPJ:', error);
      setCnpjAvailable(false);
      setCnpjHelperText('Erro ao verificar disponibilidade do CNPJ');
      setCnpjError(true);
    } finally {
      setCnpjVerifying(false);
    }
  };

  const handleChange = (field: keyof FormDataType) => (event: any) => {
    const value = event?.target ? event.target.value : event;

    if (field === 'maxCapacity' && parseInt(value) < 0) return;
    if (field === 'minCapacity' && parseInt(value) < 0) return;

    if (field === 'enterpriseNumber') {
      const cleanCNPJ = value.replace(/\D/g, '');
      const cleanOriginalCNPJ = originalEnterpriseNumber.replace(/\D/g, '');

      if (mode === 'edit' && cleanOriginalCNPJ && cleanCNPJ === cleanOriginalCNPJ) {
        setCnpjError(false);
        setCnpjHelperText('');
        setCnpjAvailable(true);
      } else if (cleanCNPJ.length === 14 && !validateCNPJ(value)) {
        setCnpjError(true);
        setCnpjHelperText('CNPJ inválido');
        setCnpjAvailable(null);
      } else if (cleanCNPJ.length === 14 && validateCNPJ(value)) {
        setCnpjError(false);
        setCnpjHelperText('');
        verifyCNPJAvailability(value);
      } else {
        setCnpjError(false);
        setCnpjHelperText('');
        setCnpjAvailable(null);
      }
    }

    if (field === 'responsibleEmail') {
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      if (!emailRegex.test(value)) {
        setError(true);
        setHelperText('Informe um e-mail válido');
      } else {
        setError(false);
        setHelperText('');
      }
    }

    if (field === 'fantasyName' && value.length > 255) {
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ==================== FORM VALIDATION LOGIC ====================
  const folderValidation = () => {
    if (folders.length > PLAN_RULES().maxFolders) return false;
    return folders.every(
      (folder: Folder) =>
        folder.images.length >= IMAGE_MIN_LIMIT && folder.images.length <= IMAGE_MAX_LIMIT,
    );
  };

  const getStepBlockingReasons = () => {
    const reasons: string[] = [];

    if (activeStep === 0) {
      const cleanCNPJ = formData.enterpriseNumber.replace(/\D/g, '');
      const cleanPhone = formData.responsiblePhone.replace(/\D/g, '');
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      const descriptionLength = formData.description.trim().length;

      if (!formData.fantasyName.trim()) reasons.push('Preencha o Nome Fantasia.');
      if (!formData.socialReason.trim()) reasons.push('Preencha a Razão Social.');
      if (!cleanCNPJ) reasons.push('Preencha o CNPJ.');
      if (cleanCNPJ.length > 0 && cleanCNPJ.length !== 14)
        reasons.push('O CNPJ deve ter 14 dígitos.');
      if (cnpjVerifying) reasons.push('Aguarde a verificação do CNPJ.');
      if (cnpjError) reasons.push(cnpjHelperText || 'O CNPJ informado é inválido.');
      if (!formData.description.trim()) reasons.push('Preencha a descrição do espaço.');
      if (descriptionLength > MAX_LENGTH)
        reasons.push(`A descrição deve ter no máximo ${MAX_LENGTH} caracteres.`);
      if (!formData.responsibleName.trim()) reasons.push('Preencha o Nome do Responsável.');
      if (!formData.responsibleEmail.trim()) reasons.push('Preencha o E-mail do Responsável.');
      if (formData.responsibleEmail.trim() && !emailRegex.test(formData.responsibleEmail))
        reasons.push('Informe um e-mail válido para o responsável.');
      if (!cleanPhone) reasons.push('Preencha o WhatsApp do Responsável.');
      if (cleanPhone.length > 0 && cleanPhone.length !== 11)
        reasons.push('O WhatsApp deve ter 11 dígitos (DDD + número).');
    }

    if (activeStep === 1) {
      const cleanPostalCode = formData.postalCode.replace(/\D/g, '');
      if (formData.cityId <= 0) reasons.push('Selecione uma cidade.');
      if (formData.districtId <= 0) reasons.push('Selecione um bairro.');
      if (!formData.address.trim()) reasons.push('Preencha o endereço do espaço.');
      if (!cleanPostalCode) reasons.push('Preencha o CEP.');
      if (cleanPostalCode.length > 0 && cleanPostalCode.length !== 8)
        reasons.push('O CEP deve ter 8 dígitos.');
    }

    if (activeStep === 2) {
      if (formData.estabilishmentType <= 0) reasons.push('Selecione a categoria do espaço.');
      if (formData.events.length <= 0) reasons.push('Selecione ao menos um evento mais adequado.');
    }

    if (activeStep === 3) {
      const rules = PLAN_RULES();

      if (rules.maxFolders > 0 && folders.length > rules.maxFolders) {
        reasons.push(`Seu plano permite no máximo ${rules.maxFolders} pasta(s) de galeria.`);
      }

      if (rules.maxFolders > 0) {
        folders.forEach((folder) => {
          if (folder.images.length < IMAGE_MIN_LIMIT) {
            reasons.push(
              `A pasta ${folder.name} precisa de pelo menos ${IMAGE_MIN_LIMIT} imagens.`,
            );
          }

          if (folder.images.length > IMAGE_MAX_LIMIT) {
            reasons.push(`A pasta ${folder.name} pode ter no máximo ${IMAGE_MAX_LIMIT} imagens.`);
          }
        });
      }

      if (rules.blueprint && !blueprint) {
        reasons.push('Envie a Planta Baixa.');
      }

      if (rules.banner && !banner) {
        reasons.push('Envie a Foto Principal.');
      }

      if (!brand) {
        reasons.push('Envie a Logomarca.');
      }
    }

    return reasons;
  };

  const checkForm = () => {
    setCanJumpToNext(getStepBlockingReasons().length === 0);
  };

  // ==================== LOAD PLACE DATA (EDIT MODE) ====================
  useEffect(() => {
    if (mode === 'edit' && placeId) {
      PlaceService.detailFull(placeId)
        .then((data: any) => {
          console.log('🔍 Dados do espaço carregados:', data);
          console.log('📸 Mídias:', data.placeMedias);
          console.log('📁 Coleções:', data.collections);

          const responsiblePhoneDigits = (data.responsiblePhone ?? '').replace(/\D/g, '');
          const normalizedResponsiblePhone = responsiblePhoneDigits.startsWith('55')
            ? responsiblePhoneDigits.slice(2)
            : responsiblePhoneDigits;

          setFormData((prev) => ({
            ...prev,
            id: data.id,
            description: data.description ?? '',
            address: data.address ?? '',
            addressNumber: data.addressNumber ?? '',
            postalCode: data.postalCode ?? '',
            complement: data.complement ?? '',
            minCapacity: data.minCapacity ?? 10,
            maxCapacity: data.maxCapacity ?? 200,
            hasAirConditioner: !!data.hasAirConditioner,
            hasBuffet: !!data.hasBuffet,
            hasParking: !!data.hasParking,
            hasOutdoor: !!data.hasOutdoor,
            hasSound: !!data.hasSound,
            hasRoom: !!data.hasRoom,
            area: data.area ?? 0,
            socialReason: data.socialReason ?? '',
            fantasyName: data.fantasyName ?? '',
            enterpriseNumber: data.enterpriseNumber ?? '',
            responsibleName: data.responsibleName ?? '',
            responsibleEmail: data.responsibleEmail ?? '',
            responsiblePhone: normalizedResponsiblePhone,
            cityId: data.cityId ?? 0,
            regionId: data.regionId ?? 0,
            districtId: data.districtId ?? 0,
            events: data.placeEventTypes?.map((event: any) => event.eventTypeId) ?? [],
            youtube: data.youtube ?? '',
            estabilishmentType: data.placeEstabilishmentTypes?.[0]?.estabilishmentTypeId ?? 0,
            userAccountId: data.userAccountId ?? 1,
            status: data.status ?? 'INACTIVE',
            type: data.type ?? PlaceTypeEnum.FREE,
          }));
          setOriginalPlaceType(data.type ?? null);
          setOriginalEnterpriseNumber(data.enterpriseNumber ?? '');

          // Carregar blueprint/banner/brand
          if (data?.blueprint?.url || data?.blueprintUrl) {
            console.log('🖼️ Carregando blueprint:', data.blueprint);
            setBlueprint({
              id: data?.blueprint?.id,
              file: null,
              url: data.blueprint?.url ?? data.blueprintUrl,
            });
          }
          if (data?.banner?.url || data?.bannerUrl) {
            console.log('🖼️ Carregando banner:', data.banner);
            setBanner({
              id: data?.banner?.id,
              file: null,
              url: data.banner?.url ?? data.bannerUrl,
            });
          }
          if (data?.brand?.url || data?.brandUrl) {
            console.log('🖼️ Carregando brand:', data.brand);
            setBrand({
              id: data?.brand?.id,
              file: null,
              url: data.brand?.url ?? data.brandUrl,
            });
          }

          // Carregar collections
          if (data.collections && data.collections.length > 0) {
            console.log('📁 Carregando coleções:', data.collections);
            const loadedFolders = data.collections.map((collection: any) => ({
              id: collection.id,
              name: collection.name,
              images: Array.isArray(collection?.medias)
                ? collection.medias.map((media: any) => ({
                    id: media.id,
                    file: null,
                    url: media.url,
                  }))
                : [],
              canExclude: !(collection?.name === 'Nosso Espaço'),
            }));
            setFolders(
              loadedFolders.length > 0
                ? loadedFolders
                : [{ name: 'Nosso Espaço', images: [], canExclude: false }],
            );
            console.log('✅ Pastas configuradas:', loadedFolders);
          } else {
            console.log('⚠️ Nenhuma coleção encontrada, usando pastas padrão');
          }
        })
        .catch((error: any) => {
          console.error('❌ Erro ao carregar dados do espaço:', error);
          console.error('Detalhes do erro:', error.response?.data);
          alert('Erro ao carregar dados do espaço: ' + error.response?.data?.detail);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [mode, placeId]);

  // ==================== LOAD DROPDOWNS ====================
  useEffect(() => {
    CityService.search().then((response) => setCities(response.data));
    EventService.search().then((response) => setEvents(response.data));
    EstabilishmentService.search().then((response) => setEstablishments(response.data));
  }, []);

  // ==================== UPDATE REGIONS WHEN CITY CHANGES ====================
  useEffect(() => {
    setRegions([]);
    setDistricts([]);
    if (formData.cityId) {
      RegionService.searchByCity(Number(formData.cityId)).then((response) =>
        setRegions(response.data),
      );
    }
  }, [formData.cityId]);

  // ==================== UPDATE DISTRICTS WHEN REGION/CITY CHANGES ====================
  useEffect(() => {
    setDistricts([]);
    if (formData.regionId || formData.cityId) {
      DistrictService.search(Number(formData.regionId), Number(formData.cityId)).then((response) =>
        setDistricts(response.data),
      );
    }
  }, [formData.regionId, formData.cityId]);

  // ==================== AUTO-SELECT CITY FROM VIACEP ====================
  useEffect(() => {
    if (cidadeViaCep && cities && cities.length > 0) {
      console.log('🔍 Procurando cidade:', cidadeViaCep);
      console.log(
        '📋 Cidades disponíveis:',
        cities.map((c: any) => ({ name: c.name, normalized: normalize(c.name) })),
      );

      const foundCity: any = cities.find((c: any) => {
        const normalizedCityName = normalize(c.name);
        return normalizedCityName === cidadeViaCep;
      });

      if (foundCity) {
        console.log('✅ Cidade encontrada:', foundCity);
        setFormData((prev) => ({ ...prev, cityId: foundCity.id }));
      } else {
        console.log('❌ Cidade não encontrada');
      }
    }
  }, [cidadeViaCep, cities]);

  // ==================== AUTO-SELECT DISTRICT FROM VIACEP ====================
  useEffect(() => {
    if (bairroViaCep && districts) {
      const foundDistrict: any = districts.find((d: any) => normalize(d.name) === bairroViaCep);
      if (foundDistrict) {
        setFormData((prev) => ({ ...prev, districtId: foundDistrict.id }));
      }
    }
  }, [formData.cityId, districts, bairroViaCep]);

  // ==================== TRIGGER FORM VALIDATION ====================
  useEffect(() => {
    checkForm();
  }, [formData, activeStep, blueprint, banner, brand, folders]);

  // ==================== MEDIA UPLOAD HANDLERS ====================
  const handleBlueprintUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.size <= IMAGE_SIZE_LIMIT_MB) {
      const url = URL.createObjectURL(file);
      setBlueprint({ id: new Date().getTime(), file, url });
    }
  };

  const handleBannerUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.size <= IMAGE_SIZE_LIMIT_MB) {
      const url = URL.createObjectURL(file);
      setBanner({ id: new Date().getTime(), file, url });
    }
  };

  const handleBrandUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.size <= IMAGE_SIZE_LIMIT_MB) {
      const url = URL.createObjectURL(file);
      setBrand({ id: new Date().getTime(), file, url });
    }
  };

  const handleRemoveBlueprint = () => {
    if (blueprint?.id && !blueprint.file && typeof blueprint.id === 'number') {
      setImagesToRemove((prev) => [...prev, { imageId: blueprint.id as number }]);
    }
    if (blueprint) URL.revokeObjectURL(blueprint.url);
    setBlueprint(null);
  };

  const handleRemoveBanner = () => {
    if (banner?.id && !banner.file && typeof banner.id === 'number') {
      setImagesToRemove((prev) => [...prev, { imageId: banner.id as number }]);
    }
    if (banner) URL.revokeObjectURL(banner.url);
    setBanner(null);
  };

  const handleRemoveBrand = () => {
    if (brand?.id && !brand.file && typeof brand.id === 'number') {
      setImagesToRemove((prev) => [...prev, { imageId: brand.id as number }]);
    }
    if (brand) URL.revokeObjectURL(brand.url);
    setBrand(null);
  };

  // ==================== FOLDER HANDLERS ====================
  const handleAddFolder = () => {
    if (folders.length >= PLAN_RULES().maxFolders) return;
    setFolders((prev) => [
      ...prev,
      { name: `Pasta ${prev.length + 1}`, images: [], canExclude: true },
    ]);
  };

  const handleRemoveFolder = (folderIndex: number) => {
    const folder = folders[folderIndex];
    if (folder.id) {
      setFoldersToRemove((prev) => [...prev, folder.id!]);
    }
    setFolders((prev) => prev.filter((_, i) => i !== folderIndex));
  };

  const handleRenameFolder = (folderIndex: number) => {
    const currentName = folders[folderIndex].name;
    const newName = prompt('Digite o novo nome da pasta:', currentName);
    if (newName && newName.trim() !== '') {
      setFolders((prev) => {
        const updated = [...prev];
        updated[folderIndex] = { ...updated[folderIndex], name: newName.trim() };
        return updated;
      });
      // Se a pasta já existe no servidor, marcar para rename
      if (folders[folderIndex].id) {
        setFoldersToRename((prev) => {
          const filtered = prev.filter((f) => f.id !== folders[folderIndex].id);
          return [...filtered, { id: folders[folderIndex].id!, newName: newName.trim() }];
        });
      }
    }
  };

  // ==================== IMAGE HANDLERS ====================
  const handleAddImages = (folderIndex: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);

    if (files.length === 0) return;

    setFolders((prev) => {
      const updated = [...prev];
      const folder = updated[folderIndex];
      if (!folder) return prev;

      // Calcula quantas imagens podem ser adicionadas
      const remainingSlots = IMAGE_MAX_LIMIT - folder.images.length;
      if (remainingSlots <= 0) return prev;

      // Cria novas imagens com IDs únicos
      const newImages = files
        .slice(0, remainingSlots)
        .filter((file) => file.size <= IMAGE_SIZE_LIMIT_MB)
        .map((file) => ({
          id: `${Date.now()}-${Math.random()}-${file.name}`,
          file,
          url: URL.createObjectURL(file),
        }));

      // Verifica se há duplicatas antes de adicionar
      const existingNames = new Set(folder.images.map((img) => img.file?.name || ''));
      const uniqueNewImages = newImages.filter((img) => !existingNames.has(img.file.name));

      if (uniqueNewImages.length > 0) {
        folder.images = [...folder.images, ...uniqueNewImages];
      }

      return updated;
    });

    // Reseta o input
    input.value = '';
  };

  const handleRemoveImage = (folderIndex: number, imageId: number | string) => {
    setFolders((prev) => {
      const updated = [...prev];
      const folder = updated[folderIndex];
      if (!folder) return prev;

      const img = folder.images.find((i) => i.id === imageId);
      if (img) {
        URL.revokeObjectURL(img.url);
        if (img.id && !img.file && typeof img.id === 'number') {
          setImagesToRemove((prevImages) => {
            const newRemove = { imageId: img.id as number, folderId: folder.id };
            return [...prevImages, newRemove];
          });
        }
      }

      folder.images = folder.images.filter((i) => i.id !== imageId);
      return updated;
    });
  };

  // ==================== NAVIGATION ====================
  const handleNext = () => {
    if (activeStep < steps.length - 1) setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (activeStep > 0) setActiveStep((prev) => prev - 1);
  };

  // ==================== SAVE PLACE ====================
  const savePlace = async () => {
    setSaving(true);
    try {
      const responsiblePhoneDigits = formData.responsiblePhone.replace(/\D/g, '');
      const normalizedResponsiblePhone = responsiblePhoneDigits
        ? responsiblePhoneDigits.startsWith('55')
          ? responsiblePhoneDigits
          : `55${responsiblePhoneDigits}`
        : '';
      const basePayload = {
        ...formData,
        responsiblePhone: normalizedResponsiblePhone,
        type: selectedPlan?.placeType ?? formData.type,
      };
      const response =
        mode === 'create'
          ? await PlaceService.create(basePayload, effectivePromotionalCode || undefined)
          : await PlaceService.update({
              ...basePayload,
              id: placeId,
              imagesToRemove: imagesToRemove.map((img) => img.imageId),
              foldersToRemove: foldersToRemove,
            } as any);

      const savedPlaceId = mode === 'create' ? response.id : placeId!;
      const uploadPromises: Promise<any>[] = [];

      // Upload new media files
      if (blueprint?.file) {
        uploadPromises.push(
          MediaService.upload(savedPlaceId, blueprint.file, MediaTypeEnum.BLUEPRINT, token),
        );
      }
      if (banner?.file) {
        uploadPromises.push(
          MediaService.upload(savedPlaceId, banner.file, MediaTypeEnum.BANNER, token),
        );
      }
      if (brand?.file) {
        uploadPromises.push(
          MediaService.upload(savedPlaceId, brand.file, MediaTypeEnum.BRAND, token),
        );
      }

      // Handle folder images
      for (const folder of folders) {
        if (folder.images.length > 0) {
          const newImages = folder.images.filter((img) => img.file);

          if (newImages.length > 0) {
            if (folder.id) {
              for (const image of newImages) {
                uploadPromises.push(
                  MediaService.uploadWithColletion(
                    savedPlaceId,
                    folder.id,
                    image.file!,
                    MediaTypeEnum.MEDIA,
                    token,
                  ),
                );
              }
            } else {
              const collection = await MediaService.createCollection(savedPlaceId, folder.name);
              for (const image of newImages) {
                uploadPromises.push(
                  MediaService.uploadWithColletion(
                    savedPlaceId,
                    collection.id,
                    image.file!,
                    MediaTypeEnum.MEDIA,
                    token,
                  ),
                );
              }
            }
          }
        }
      }

      // Delete removed folders
      for (const folderId of foldersToRemove) {
        uploadPromises.push(MediaService.deleteCollection(folderId));
      }

      // Rename folders
      for (const folderRename of foldersToRename) {
        uploadPromises.push(MediaService.editCollection(folderRename.id, folderRename.newName));
      }

      await Promise.all(uploadPromises);

      // Em criação ou edição de PRE_REGISTRED com plano selecionado, abrir fluxo de pagamento.
      const isPreRegisteredEdit = mode === 'edit' && originalPlaceType === 'PRE_REGISTRED';
      const shouldOpenPaymentFlow =
        !bypassPaymentFlow && !!selectedPlan && (mode === 'create' || isPreRegisteredEdit);

      if (shouldOpenPaymentFlow) {
        setPaymentPlaceId(savedPlaceId);
        const planToUse = plans.find((p) => p.placeType === selectedPlan?.placeType) || null;

        if (planToUse?.type === 'FREE') {
          navigate(shouldRedirectWithBackpack ? '/listar/espaco?backpack=339' : '/listar/espaco');
          return;
        }

        setPaymentPlan(planToUse);
        setOpenPaymentModal(true);
      } else {
        navigate('/listar/espaco');
      }
    } catch (error: any) {
      alert('Erro ao salvar espaço: ' + error.response?.data?.detail);
    } finally {
      setSaving(false);
    }
  };

  // ==================== RENDER STEP CONTENT ====================
  const renderStepContent = (step: number) => {
    switch (step) {
      // ====== STEP 0: Dados Básicos ======
      case 0:
        return (
          <Box display="flex" flexWrap="wrap" gap={2} p={1}>
            <Box flex={{ xs: '1 1 100%', md: '1 1 100%' }} minWidth={{ xs: '100%', md: 0 }}>
              <TextField
                label="Nome Fantasia"
                variant="standard"
                fullWidth
                required
                value={formData.fantasyName}
                onChange={handleChange('fantasyName')}
                onBlur={handleBlur('fantasyName')}
                error={!!getError('fantasyName')}
              />
            </Box>
            <Box flex={{ xs: '1 1 100%', md: '1 1 50%' }} minWidth={{ xs: '100%', md: 0 }}>
              <TextField
                label="Razão Social"
                variant="standard"
                fullWidth
                required
                value={formData.socialReason}
                onChange={handleChange('socialReason')}
                onBlur={handleBlur('socialReason')}
                error={!!getError('socialReason')}
              />
            </Box>
            <Box flex={{ xs: '1 1 100%', md: '1 1 50%' }} minWidth={{ xs: '100%', md: 0 }}>
              <PatternFormat
                value={formData.enterpriseNumber}
                onValueChange={(values: any) => handleChange('enterpriseNumber')(values.value)}
                format="##.###.###/####-##"
                mask="_"
                customInput={TextField}
                label="CNPJ"
                variant="standard"
                fullWidth
                required
                onBlur={handleBlur('enterpriseNumber')}
                error={cnpjError || !!getError('enterpriseNumber')}
                helperText={cnpjHelperText}
                disabled={cnpjVerifying}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {cnpjVerifying && <CircularProgress size={20} />}
                      {cnpjAvailable === true && (
                        <Tooltip title="CNPJ disponível">
                          <Typography sx={{ color: '#4caf50', fontWeight: 'bold' }}>✓</Typography>
                        </Tooltip>
                      )}
                      {cnpjAvailable === false && (
                        <Tooltip title={cnpjHelperText}>
                          <Typography sx={{ color: '#f44336', fontWeight: 'bold' }}>✗</Typography>
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box flex={{ xs: '1 1 100%', md: '1 1 50%' }} minWidth={{ xs: '100%', md: 0 }}>
              <TextField
                label="Nome do Responsável"
                variant="standard"
                fullWidth
                required
                value={formData.responsibleName}
                onChange={handleChange('responsibleName')}
                onBlur={handleBlur('responsibleName')}
                error={!!getError('responsibleName')}
              />
            </Box>
            <Box flex={{ xs: '1 1 100%', md: '1 1 50%' }} minWidth={{ xs: '100%', md: 0 }}>
              <TextField
                label="E-mail do Responsável"
                type="email"
                variant="standard"
                fullWidth
                required
                value={formData.responsibleEmail}
                onChange={handleChange('responsibleEmail')}
                onBlur={handleBlur('responsibleEmail')}
                error={error || !!getError('responsibleEmail')}
                helperText={helperText}
              />
            </Box>
            <Box flex={{ xs: '1 1 100%', md: '1 1 50%' }} minWidth={{ xs: '100%', md: 0 }}>
              <PatternFormat
                value={formData.responsiblePhone}
                onValueChange={(values: any) => handleChange('responsiblePhone')(values.value)}
                format="+55 (##) #####-####"
                mask="_"
                customInput={TextField}
                label="Whatsapp do Responsável"
                variant="standard"
                fullWidth
                required
                onBlur={handleBlur('responsiblePhone')}
                error={!!getError('responsiblePhone')}
              />
            </Box>
            <Box
              flex="1 1 100%"
              width="100%"
              onClick={() => editorDomRef.current?.editor?.commands.focus()}
            >
              <Typography variant="subtitle1" gutterBottom>
                Descrição *
                <Tooltip
                  arrow
                  title="Forneça uma descrição detalhada do espaço, incluindo suas características, serviços oferecidos e qualquer informação relevante que possa ajudar os usuários a entender melhor o local."
                >
                  <IconButton edge="end" size="small" tabIndex={-1}>
                    <HelpOutline sx={{ width: 20 }} />
                  </IconButton>
                </Tooltip>
              </Typography>
              <RichTextEditor
                ref={editorDomRef}
                extensions={[StarterKit]}
                content={formData.description}
                sx={{
                  minHeight: 200,
                  width: '100%',
                  lineBreak: 'anywhere',
                  '& div': { lineBreak: 'anywhere' },
                }}
                onUpdate={({ editor }) => {
                  const text = editor.getText();
                  setFormData((prev) => ({ ...prev, description: text }));
                  setValue(text);
                }}
              />
              <Typography
                color={
                  value.replace(/<[^>]+>/g, '').length > MAX_LENGTH ? 'error' : 'textSecondary'
                }
              >
                {value.replace(/<[^>]+>/g, '').length} / {MAX_LENGTH} caracteres
              </Typography>
            </Box>
          </Box>
        );

      // ====== STEP 1: Endereço ======
      case 1:
        return (
          <Box display="flex" flexWrap="wrap" gap={2} p={1}>
            <Box flex={{ xs: '1 1 100%', md: '1 1 30%' }} minWidth={{ xs: '100%', md: 0 }}>
              <PatternFormat
                value={formData.postalCode}
                onValueChange={(values: any) => {
                  const cepValue = values.value;
                  setFormData({ ...formData, postalCode: cepValue });
                  if (cepValue && cepValue.length === 8) {
                    fetchAddressByCep(cepValue);
                  }
                }}
                format="#####-###"
                mask="_"
                customInput={TextField}
                label="CEP"
                variant="standard"
                fullWidth
                required
                onBlur={handleBlur('postalCode')}
                error={!!getError('postalCode')}
              />
            </Box>
            <Box flex={{ xs: '1 1 100%', md: '1 1 35%' }} minWidth={{ xs: '100%', md: 0 }}>
              <FormControl fullWidth>
                <Autocomplete
                  options={cities}
                  getOptionLabel={(c: any) => c.name || ''}
                  value={cities.find((c: any) => c.id === formData.cityId) || null}
                  onChange={(e, v) => setFormData({ ...formData, cityId: v?.id || 0 })}
                  renderInput={(params) => (
                    <TextField
                      required
                      {...params}
                      label="Cidade"
                      variant="standard"
                      onBlur={handleBlur('cityId')}
                      error={!!getError('cityId')}
                    />
                  )}
                />
              </FormControl>
            </Box>
            {districts.length > 0 && (
              <Box flex={{ xs: '1 1 100%', md: '1 1 35%' }} minWidth={{ xs: '100%', md: 0 }}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={districts}
                    getOptionLabel={(d: any) => d.name || ''}
                    value={districts.find((d: any) => d.id === formData.districtId) || null}
                    onChange={(e, v) => setFormData({ ...formData, districtId: v?.id || 0 })}
                    renderInput={(params) => (
                      <TextField
                        required
                        {...params}
                        label="Bairro"
                        variant="standard"
                        onBlur={handleBlur('districtId')}
                        error={!!getError('districtId')}
                      />
                    )}
                  />
                </FormControl>
              </Box>
            )}
            <Box flex={{ xs: '1 1 100%', md: '1 1 70%' }} minWidth={{ xs: '100%', md: 0 }}>
              <TextField
                label="Endereço do Espaço"
                variant="standard"
                fullWidth
                required
                value={formData.address}
                onChange={handleChange('address')}
                onBlur={handleBlur('address')}
                error={!!getError('address')}
              />
            </Box>
            <Box flex={{ xs: '1 1 100%', md: '1 1 15%' }} minWidth={{ xs: '100%', md: 0 }}>
              <TextField
                label="Número"
                type="number"
                variant="standard"
                fullWidth
                value={formData.addressNumber}
                onChange={handleChange('addressNumber')}
              />
            </Box>
            <Box flex={{ xs: '1 1 100%', md: '1 1 100%' }} minWidth={{ xs: '100%', md: 0 }}>
              <TextField
                label="Complemento"
                variant="standard"
                fullWidth
                value={formData.complement}
                onChange={handleChange('complement')}
              />
            </Box>
          </Box>
        );

      // ====== STEP 2: Informações Adicionais ======
      case 2:
        return (
          <Box display="flex" flexWrap="wrap" gap={2} p={1}>
            <Box flex={{ xs: '1 1 100%', md: '1 1 40%' }}>
              <TextField
                label="Capacidade Mínima"
                type="number"
                variant="standard"
                fullWidth
                value={formData.minCapacity}
                onChange={handleChange('minCapacity')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        arrow
                        title="Indica a capacidade mínima de pessoas que o espaço pode acomodar."
                      >
                        <IconButton edge="end" size="small" tabIndex={-1}>
                          <HelpOutline sx={{ width: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box flex={{ xs: '1 1 100%', md: '1 1 40%' }}>
              <TextField
                label="Capacidade Máxima"
                type="number"
                variant="standard"
                fullWidth
                value={formData.maxCapacity}
                onChange={handleChange('maxCapacity')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ ml: 0, pr: 0 }}>
                      <Tooltip
                        arrow
                        title="Indica a capacidade máxima de pessoas que o espaço pode acomodar."
                      >
                        <IconButton
                          edge="end"
                          size="small"
                          tabIndex={-1}
                          sx={{ padding: '2px', marginLeft: 0 }}
                        >
                          <HelpOutline sx={{ width: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {[
              {
                name: 'Possui Ar condicionado ?',
                value: 'hasAirConditioner' as const,
                help: 'Indica se o espaço possui sistema de ar condicionado instalado.',
              },
              {
                name: 'Possui Buffet Próprio ?',
                value: 'hasBuffet' as const,
                help: 'Indica se o espaço oferece serviço de buffet para eventos.',
              },
              {
                name: 'Possui Estacionamento ?',
                value: 'hasParking' as const,
                help: 'Indica se o espaço dispõe de estacionamento para os visitantes.',
              },
              {
                name: 'Possui Área externa ?',
                value: 'hasOutdoor' as const,
                help: 'Indica se o espaço possui uma área externa disponível.',
              },
              {
                name: 'Possui Sistema de som ?',
                value: 'hasSound' as const,
                help: 'Indica se o espaço está equipado com sistema de som.',
              },
              {
                name: 'Possui Mais de Um Salão ?',
                value: 'hasRoom' as const,
                help: 'Indica se o espaço possui múltiplas salas',
              },
            ].map((f) => (
              <Box key={f.value} flex={{ xs: '1 1 100%', md: '1 1 40%' }}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={[
                      { name: 'Sim', value: true },
                      { name: 'Não', value: false },
                    ]}
                    getOptionLabel={(o: any) => o.name || ''}
                    fullWidth
                    value={
                      formData[f.value]
                        ? { name: 'Sim', value: true }
                        : { name: 'Não', value: false }
                    }
                    onChange={(e, v) => setFormData({ ...formData, [f.value]: v?.value || false })}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={f.name}
                        variant="standard"
                        sx={{ width: '100%' }}
                        InputProps={{ ...params.InputProps }}
                      />
                    )}
                  />
                </FormControl>
              </Box>
            ))}
            <Box flex={{ xs: '1 1 100%', md: '1 1 100%' }}>
              <FormControl fullWidth>
                <Autocomplete
                  options={estabilishments}
                  getOptionLabel={(e: any) => e.name || ''}
                  value={
                    estabilishments.find((e: any) => e.id === formData.estabilishmentType) || null
                  }
                  onChange={(e, v) => setFormData({ ...formData, estabilishmentType: v?.id || 0 })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Categoria do Espaço"
                      required
                      variant="standard"
                      onBlur={handleBlur('estabilishmentType')}
                      error={!!getError('estabilishmentType')}
                      InputProps={{
                        ...params.InputProps,
                      }}
                    />
                  )}
                />
              </FormControl>
            </Box>
            <Box flex={{ xs: '1 1 100%', md: '1 1 100%' }}>
              <FormControl fullWidth>
                <Autocomplete
                  options={events.filter((ev: any) => !formData.events.includes(ev.id))}
                  multiple
                  disableCloseOnSelect
                  getOptionLabel={(ev: any) => ev.name || ''}
                  value={events.filter((ev: any) => formData.events.includes(ev.id)) || []}
                  onChange={(e, v) =>
                    setFormData({ ...formData, events: v.map((ev: any) => ev.id) })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Eventos Mais Adequados"
                      required
                      variant="standard"
                      onBlur={handleBlur('events')}
                      error={!!getError('events')}
                      helperText={getError('events') && 'Selecione ao menos um evento'}
                      InputProps={{ ...params.InputProps }}
                    />
                  )}
                />
              </FormControl>
            </Box>
          </Box>
        );

      // ====== STEP 3: Mídias ======
      case 3:
        return (
          <Box
            sx={{
              width: '100%',
              pt: 2,
              pb: 2,
              boxSizing: 'border-box',
            }}
          >
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Gerenciar Mídias
            </Typography>

            <Box sx={{ width: '100%' }}>
              <Paper elevation={3} sx={{ p: 2, width: '100%', mb: 2, boxSizing: 'border-box' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Logomarca *
                  </Typography>
                  {brand && (
                    <IconButton color="error" size="small" onClick={handleRemoveBrand}>
                      <CancelOutlined />
                    </IconButton>
                  )}
                </Box>
                {!brand && (
                  <Button variant="outlined" component="label" size="small">
                    Upload Marca
                    <input hidden type="file" accept="image/*" onChange={handleBrandUpload} />
                  </Button>
                )}
                {brand && (
                  <Box mt={2} width="100%" maxWidth={400} borderRadius={1} p={2}>
                    <BrandImage src={brand.url} />
                  </Box>
                )}
                <Typography variant="body2" mt={2} color="textSecondary">
                  Esta imagem será exibida na capa do site e nos detalhes do espaço.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  (Tamanho recomendado: 400x400px)
                </Typography>
              </Paper>

              {!!PLAN_RULES().banner && (
                <Paper elevation={3} sx={{ p: 2, width: '100%', mb: 2, boxSizing: 'border-box' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Foto Principal *
                      <Tooltip
                        arrow
                        title="É a imagem que servirá como capa de fundo na página do seu espaço."
                      >
                        <IconButton edge="end" size="small" tabIndex={-1}>
                          <HelpOutline sx={{ width: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </Typography>
                    {banner && (
                      <IconButton color="error" size="small" onClick={handleRemoveBanner}>
                        <CancelOutlined />
                      </IconButton>
                    )}
                  </Box>
                  {!banner && (
                    <Button variant="outlined" component="label" size="small">
                      Upload Banner
                      <input hidden type="file" accept="image/*" onChange={handleBannerUpload} />
                    </Button>
                  )}
                  {banner && (
                    <Box mt={2}>
                      <HeaderImage src={banner.url} />
                    </Box>
                  )}
                  <Typography variant="body2" mt={2} color="textSecondary">
                    Essa imagem será a capa na página do seu espaço
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    (Tamanho recomendado: 1920x400px)
                  </Typography>
                </Paper>
              )}

              {!!PLAN_RULES().blueprint && (
                <Paper elevation={3} sx={{ p: 2, width: '100%', mb: 2, boxSizing: 'border-box' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Planta Baixa
                      <Tooltip
                        arrow
                        title="Desenho em escala, mostrando uma vista de cima, das relações entre salas, espaços e outras características fisica do local"
                      >
                        <IconButton edge="end" size="small" tabIndex={-1}>
                          <HelpOutline sx={{ width: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </Typography>
                    {blueprint && (
                      <IconButton color="error" size="small" onClick={handleRemoveBlueprint}>
                        <CancelOutlined />
                      </IconButton>
                    )}
                  </Box>
                  {!blueprint && (
                    <Button variant="outlined" component="label" size="small">
                      Upload Planta Baixa
                      <input hidden type="file" accept="image/*" onChange={handleBlueprintUpload} />
                    </Button>
                  )}
                  {blueprint && (
                    <Box mt={2} p={2} width="100%" borderRadius={1} overflow="hidden">
                      <SquareImage src={blueprint.url} />
                    </Box>
                  )}
                  <Typography variant="body2" mt={2} color="textSecondary">
                    Essa imagem será exibida nas características do espaço, caso seja enviada.
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    (Tamanho recomendado: 600x600px)
                  </Typography>
                </Paper>
              )}
            </Box>

            {PLAN_RULES().maxFolders > 0 && (
              <>
                <Typography variant="h6" fontWeight="bold" my={2}>
                  Galeria de Imagens
                </Typography>
                <Box sx={{ width: '100%' }}>
                  {folders.map((folder, folderIndex) => (
                    <Paper
                      key={folderIndex}
                      elevation={3}
                      sx={{
                        p: 2,
                        width: '100%',
                        mb: 2,
                        boxSizing: 'border-box',
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {folder.name}
                        </Typography>
                        <Box display="flex" gap={0.5}>
                          {folder.canExclude && (
                            <IconButton
                              size="small"
                              onClick={() => handleRenameFolder(folderIndex)}
                              title="Renomear pasta"
                            >
                              <EditOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                          )}
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleRemoveFolder(folderIndex)}
                          >
                            <CancelOutlined />
                          </IconButton>
                        </Box>
                      </Box>
                      <Button variant="outlined" component="label" size="small">
                        Adicionar Imagens
                        <input
                          hidden
                          multiple
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleAddImages(folderIndex, e)}
                        />
                      </Button>
                      <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
                        {folder.images.map((img) => (
                          <Box
                            key={img.id}
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: 1,
                              overflow: 'hidden',
                              border: '1px solid #ccc',
                              position: 'relative',
                            }}
                          >
                            <img
                              src={img.url}
                              alt=""
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            <IconButton
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                backgroundColor: 'rgba(255,255,255,0.8)',
                              }}
                              onClick={() => handleRemoveImage(folderIndex, img.id!)}
                            >
                              <CancelOutlined />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                      {folder.images.length < IMAGE_MIN_LIMIT && (
                        <Typography color="error">
                          Quantidade de Imagens insuficiente o mínimo é de {IMAGE_MIN_LIMIT}{' '}
                          imagens.
                        </Typography>
                      )}
                      {folder.images.length > IMAGE_MAX_LIMIT && (
                        <Typography color="error">
                          Quantidade de Imagens excedente o máximo é de {IMAGE_MAX_LIMIT} imagens.
                        </Typography>
                      )}
                    </Paper>
                  ))}

                  {folders.length < PLAN_RULES().maxFolders && (
                    <Paper
                      sx={{
                        p: 0,
                        width: '100%',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        cursor: 'pointer',
                      }}
                      onClick={handleAddFolder}
                    >
                      <Typography variant="body2" color="primary">
                        + Criar Nova Pasta
                      </Typography>
                    </Paper>
                  )}
                </Box>
              </>
            )}

            {!!PLAN_RULES().video && (
              <Box flex={{ xs: '1 1 100%', md: '1 1 40%' }} minWidth={{ xs: '100%', md: 0 }}>
                <Typography variant="h6" fontWeight="bold" mt={2} mb={1}>
                  Vídeo do Espaço
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={1}>
                  Se você tiver um vídeo do seu espaço, pode compartilhar o link do YouTube aqui.
                  Isso pode ajudar os visitantes a ter uma melhor noção do local.
                </Typography>
                <TextField
                  label="Inclua aqui um link do vídeo de seu espaço (Youtube)"
                  variant="standard"
                  fullWidth
                  value={formData.youtube}
                  onChange={handleChange('youtube')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          arrow
                          title="É indispensável que seja um link do Youtube ex: https://www.youtube.com/watch?v=123456"
                        >
                          <IconButton edge="end" size="small" tabIndex={-1}>
                            <HelpOutline sx={{ width: 20 }} />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}
          </Box>
        );

      default:
        return <Box p={2}>Em desenvolvimento</Box>;
    }
  };

  // ==================== LOADING STATE ====================
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // ==================== MAIN RENDER ====================
  const stepBlockingReasons = canJumpToNext ? [] : getStepBlockingReasons();
  const previewBlockingReasons = stepBlockingReasons.slice(0, 3);
  const remainingBlockingReasons = stepBlockingReasons.slice(3);

  return (
    <Box
      sx={{
        bgcolor: '#F5F7FA',
        minHeight: '100vh',
        pb: { xs: 2, sm: 3 },
      }}
    >
      <Box
        sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 1.5, sm: 2, md: 4 }, pt: { xs: 1.5, md: 3 } }}
      >
        <Box
          sx={{
            bgcolor: 'white',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'clip',
            boxShadow: '0 1px 10px rgba(15, 23, 42, 0.04)',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              bgcolor: 'white',
            }}
          >
            <Button
              startIcon={<ArrowBack />}
              variant="text"
              size="small"
              onClick={() => navigate('/listar/espaco')}
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                textTransform: 'none',
                px: 0,
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: 'text.primary',
                },
              }}
            >
              Voltar para listagem
            </Button>
          </Box>

          <Box
            sx={{
              px: { xs: 2, md: 3 },
              pt: { xs: 2, md: 3 },
              pb: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            {isTablet ? (
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" fontWeight="bold">
                  Passo {activeStep + 1} de {steps.length}
                </Typography>
                <Typography variant="body2" color="primary">
                  {steps[activeStep]}
                </Typography>
              </Box>
            ) : (
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}
            <Typography variant="body2" color="textSecondary" mt={isTablet ? 1.5 : 2}>
              Campos marcados com{' '}
              <Typography component="span" color="error">
                *
              </Typography>{' '}
              são obrigatórios.
            </Typography>
          </Box>

          <Box
            sx={{
              width: '100%',
              px: { xs: 2, sm: 2.5, md: 3 },
              pb: { xs: 2.5, md: 3 },
              boxSizing: 'border-box',
            }}
          >
            {renderStepContent(activeStep)}
          </Box>

          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'rgba(255, 255, 255, 0.96)',
              backdropFilter: 'blur(6px)',
              px: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 1.5, md: 2 },
            }}
          >
            {!canJumpToNext && stepBlockingReasons.length > 0 && (
              <>
                {isSmallMobile ? (
                  <Alert
                    severity="info"
                    sx={{ width: '100%', mb: 1.25, p: 0, boxSizing: 'border-box' }}
                  >
                    <Accordion
                      expanded={expandValidationReasons}
                      onChange={(_, expanded) => setExpandValidationReasons(expanded)}
                      disableGutters
                      elevation={0}
                      sx={{
                        bgcolor: 'transparent',
                        '&:before': { display: 'none' },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={remainingBlockingReasons.length > 0 ? <ExpandMoreIcon /> : null}
                        sx={{ px: 1.5, py: 0.25 }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            Para avançar, ajuste estes itens:
                          </Typography>
                          <Box component="ul" sx={{ pl: 2, mb: 0, mt: 0.5 }}>
                            {previewBlockingReasons.map((reason, index) => (
                              <li key={`preview-${index}-${reason}`}>
                                <Typography variant="body2">{reason}</Typography>
                              </li>
                            ))}
                          </Box>
                        </Box>
                      </AccordionSummary>
                      {remainingBlockingReasons.length > 0 && (
                        <AccordionDetails sx={{ pt: 0, pb: 1, px: 1.5 }}>
                          <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                            {remainingBlockingReasons.map((reason, index) => (
                              <li key={`extra-${index}-${reason}`}>
                                <Typography variant="body2">{reason}</Typography>
                              </li>
                            ))}
                          </Box>
                        </AccordionDetails>
                      )}
                    </Accordion>
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ width: '100%', mb: 1.25, boxSizing: 'border-box' }}>
                    <Typography variant="body2" fontWeight="bold">
                      Para avançar para o próximo passo, ajuste os itens abaixo:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 0, mt: 0.5 }}>
                      {stepBlockingReasons.map((reason, index) => (
                        <li key={`${index}-${reason}`}>
                          <Typography variant="body2">{reason}</Typography>
                        </li>
                      ))}
                    </Box>
                  </Alert>
                )}
              </>
            )}

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={1.25}
              flexWrap="wrap"
              mt={0.25}
            >
              <ButtonAtom
                variant="outlined"
                label="Anterior"
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ flex: { xs: '1 1 calc(50% - 4px)', sm: 'auto' } }}
              />
              {activeStep < steps.length - 1 ? (
                <ButtonAtom
                  label="Próximo"
                  variant="contained"
                  disabled={!canJumpToNext}
                  onClick={handleNext}
                  sx={{ flex: { xs: '1 1 calc(50% - 4px)', sm: 'auto' } }}
                />
              ) : (
                <ButtonAtom
                  label={saving ? 'Salvando...' : 'Salvar'}
                  variant="contained"
                  color="primary"
                  onClick={debounce(savePlace, 200)}
                  disabled={!canJumpToNext || saving}
                  sx={{ flex: { xs: '1 1 calc(50% - 4px)', sm: 'auto' } }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {saving && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <CircularProgress size={isTablet ? 40 : 60} />
          <Typography color="white" variant={isTablet ? 'body1' : 'h6'}>
            Salvando...
          </Typography>
        </Box>
      )}

      {paymentPlaceId && paymentPlan && paymentPlan.type !== 'FREE' && (
        <PaymentFullScreenModal
          open={openPaymentModal}
          placeId={paymentPlaceId}
          selectedPlan={paymentPlan}
          onClose={() => {
            setOpenPaymentModal(false);
            setPaymentPlaceId(null);
            setPaymentPlan(null);
            navigate('/listar/espaco');
          }}
        />
      )}
    </Box>
  );
}
